// ─────────────────────────────────────────────
//  LightCards — the read-only side cards for Timeline Light.
//
//  Anesthesia Team Control (ATC)
//  Copyright © 2026 Gonzalo Martinez (gamc00@gmail.com). All rights reserved.
//  Proprietary and confidential. Not licensed for public use — see /LICENSE.
// ─────────────────────────────────────────────
//
//  The same four facts the control panel carries — breaks, the CRNA on call,
//  the MD call list, who is free — rendered as STATUS rather than as controls.
//  The board's version of the breaks card is a grid of B/L/D/WC buttons that
//  WRITE; here the same four letters are a read-out.
//
//  ⚠️ BREAKS ARE PARTLY ALERT-DERIVED, and the alert bus is scoped to the
//  ACCOUNT's facility — it cannot be pointed at another campus. When it does not
//  cover the facility being shown, these rows are not empty, they are WRONG.
//  `alertsCovered` therefore draws a refusal instead of a card. "Nobody is on a
//  break" is an assertion of safety and must never be guessed.
//
//  The MD call list carries phone numbers as tel: links on purpose: the reason a
//  CRNA opens a call list at 5 PM is to ring somebody, and a number you have to
//  retype is a number you get wrong.
// ─────────────────────────────────────────────
import React from "react";
import { BREAK_CHANNELS, stateMeta, isBeingRelieved, isCoverPending } from "../../domain/breaks";
import tokens from "../../theme/tokens";

const { color, space, radius, font } = tokens;
const { lobby } = color;

// The letters the board uses for the four channels, in the board's order.
const CHANNEL_LETTER = {
  breakfast: "B",
  lunch: "L",
  dinner: "D",
  bathroom: "WC",
};

export function Card({ title, count, children, footer }) {
  return (
    <section style={s.card}>
      <header style={s.cardHead}>
        <span style={s.cardTitle}>{title}</span>
        {count != null && <span style={s.cardCount}>{count}</span>}
      </header>
      <span style={s.cardRule} aria-hidden="true" />
      <div style={s.cardBody}>{children}</div>
      {footer && <div style={s.cardFoot}>{footer}</div>}
    </section>
  );
}

function Empty({ children }) {
  return <p style={s.empty}>{children}</p>;
}

// A refusal, not an all-clear. Used wherever the alert bus covers a different
// facility than the one on screen.
export function AlertsElsewhere({ facilityName }) {
  return (
    <p style={s.refuse}>
      Break status is coming from{" "}
      {facilityName ? <strong>{facilityName}</strong> : "another campus"}, so it
      can&apos;t be shown here. Switch facility to read it.
    </p>
  );
}

export function BreaksCard({ rows, covered, alertFacilityName, myKey }) {
  if (!covered) {
    return (
      <Card title="Breaks">
        <AlertsElsewhere facilityName={alertFacilityName} />
      </Card>
    );
  }
  const list = Array.isArray(rows) ? rows.filter(Boolean) : [];
  return (
    <Card title="Breaks" count={list.length}>
      {!list.length && <Empty>Nobody is rostered for a break yet.</Empty>}
      {list.map((r) => {
        const mine = myKey && (r.id === myKey || r.name === myKey);
        return (
          <div key={r.id || r.name} style={{ ...s.breakRow, ...(mine ? s.rowMine : {}) }}>
            <div style={s.breakWho}>
              <span style={s.breakName}>{r.name}</span>
              <span style={s.breakRoom}>
                {r.roomName || "—"}
                {/* ⚠️ "covered by" ONLY WHEN SOMEBODY IS ACTUALLY IN THE ROOM.
                    `relieverName` is set when a cover is named, so reading the
                    field alone claims coverage that does not exist yet.
                    TimelineBreaksCard has gated this on the state machine since
                    it was written; this copy never was. */}
                {r.relieverName && isBeingRelieved(r.breaks || {})
                  ? ` · covered by ${r.relieverName}`
                  : r.relieverName && isCoverPending(r.breaks || {})
                    ? ` · ${r.relieverName} on the way`
                    : ""}
              </span>
            </div>
            <div style={s.pills}>
              {BREAK_CHANNELS.map((ch) => {
                const meta = stateMeta((r.breaks || {})[ch]);
                const on = ((r.breaks || {})[ch] || "none") !== "none";
                return (
                  <span
                    key={ch}
                    style={{
                      ...s.pill,
                      ...(on ? { background: meta.color, color: "#fff", borderColor: meta.color } : {}),
                    }}
                    title={`${CHANNEL_LETTER[ch]} — ${meta.label}`}
                  >
                    {CHANNEL_LETTER[ch]}
                  </span>
                );
              })}
            </div>
          </div>
        );
      })}
    </Card>
  );
}

export function OnCallCard({ watches }) {
  const list = Array.isArray(watches) ? watches.filter(Boolean) : [];
  return (
    <Card title="CRNA on call">
      {!list.length && <Empty>Nobody is holding a standing watch today.</Empty>}
      {/* ⚠️ `ahpName` / `orRoom`, NOT `crnaName`. buildTimeline publishes these
          entries as the raw assignment row (timeline.js onCallWatch.push), and
          the first draft of this card read `crnaName` — a field that does not
          exist on them. It compiled, rendered and printed "Unassigned" beside a
          room that had somebody in it. The bench card next door was naming that
          same person from the same data. */}
      {list.map((w, i) => (
        <div key={w.crnaKey || w.ahpId || i} style={s.onCallRow}>
          <span style={s.onCallRoom}>{w.orRoom || w.roomName || "On call"}</span>
          <span style={s.onCallName}>{w.ahpName || "Nobody assigned"}</span>
        </div>
      ))}
    </Card>
  );
}

export function MdCallCard({ rows }) {
  const list = Array.isArray(rows) ? rows.filter(Boolean) : [];
  return (
    <Card title="MD call" count={list.length || null}>
      {!list.length && <Empty>No call order published for today.</Empty>}
      {list.map((m) => (
        <div key={m.id || m.name} style={s.mdRow}>
          <div style={s.mdWho}>
            <span style={s.mdName}>{m.name}</span>
            {m.phone ? (
              <a href={`tel:${String(m.phone).replace(/[^\d+]/g, "")}`} style={s.mdPhone}>
                {m.phone}
              </a>
            ) : (
              <span style={s.mdNoPhone}>No number on file</span>
            )}
          </div>
          <div style={s.mdTags}>
            {m.isAic && <span style={{ ...s.tag, ...s.tagAic }}>AIC</span>}
            {/* ⚠️ "Locum" spelled out. No call number means locum, not "last";
                the board printed "#L" once and it was read as a rank. */}
            {m.isLocum && <span style={s.tag}>Locum</span>}
            {!m.isLocum && m.callOrder != null && <span style={s.tag}>#{m.callOrder}</span>}
            {m.leaveLabel && <span style={s.mdLeave}>leaves {m.leaveLabel}</span>}
          </div>
        </div>
      ))}
    </Card>
  );
}

export function FreeNowCard({ providers }) {
  const list = Array.isArray(providers) ? providers.filter(Boolean) : [];
  return (
    <Card title="Free now" count={list.length}>
      {!list.length && <Empty>Everyone on the roster is in a room.</Empty>}
      {list.map((p) => (
        <div key={p.id || p.name} style={s.freeRow}>
          <span style={s.freeName}>{p.name}</span>
          {p.onCall && <span style={s.tag}>{p.onCallRoom ? `${p.onCallRoom} call` : "On call"}</span>}
          {p.givingBreaks && <span style={{ ...s.tag, ...s.tagBreak }}>Giving breaks</span>}
        </div>
      ))}
    </Card>
  );
}

const s = {
  card: {
    background: lobby.plaque,
    border: `1px solid ${lobby.sand}`,
    borderRadius: radius.lg,
    padding: `${space.md}px ${space.md}px ${space.sm}px`,
    marginBottom: space.md,
  },
  cardHead: { display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: space.sm },
  cardTitle: {
    fontSize: 10,
    fontWeight: font.weight.bold,
    letterSpacing: "1.6px",
    textTransform: "uppercase",
    color: lobby.brassInk,
  },
  cardCount: { fontSize: font.size.label, fontWeight: font.weight.bold, color: color.accent.primary },
  cardRule: { display: "block", height: 1, background: lobby.brass, opacity: 0.5, margin: `6px 0 ${space.sm}px` },
  cardBody: { display: "flex", flexDirection: "column", gap: 2 },
  cardFoot: { marginTop: space.sm, fontSize: font.size.label, color: color.text.muted },

  empty: { margin: 0, fontSize: font.size.label, color: color.text.muted, lineHeight: font.lineHeight.normal },
  refuse: { margin: 0, fontSize: font.size.label, color: color.surface.errorInk, lineHeight: font.lineHeight.normal },

  breakRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: space.sm,
    padding: "6px 6px",
    borderRadius: radius.sm,
  },
  rowMine: { background: "#1e40af12" },
  breakWho: { minWidth: 0, display: "flex", flexDirection: "column" },
  breakName: { fontSize: font.size.body, fontWeight: font.weight.semibold, color: color.text.primary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  breakRoom: { fontSize: 10, color: color.text.muted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  pills: { display: "flex", gap: 3, flexShrink: 0 },
  pill: {
    minWidth: 22,
    textAlign: "center",
    border: `1px solid ${color.border.default}`,
    borderRadius: radius.sm,
    padding: "2px 4px",
    fontSize: 10,
    fontWeight: font.weight.bold,
    color: color.text.muted,
    background: color.surface.raised,
  },

  onCallRow: { display: "flex", flexDirection: "column", padding: "6px 6px", borderRadius: radius.sm },
  onCallRoom: { fontSize: 10, fontWeight: font.weight.bold, letterSpacing: ".8px", color: color.accent.primary },
  onCallName: { fontSize: font.size.body, fontWeight: font.weight.semibold },

  mdRow: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: space.sm, padding: "6px 6px", borderRadius: radius.sm },
  mdWho: { minWidth: 0, display: "flex", flexDirection: "column" },
  mdName: { fontSize: font.size.body, fontWeight: font.weight.semibold, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  mdPhone: { fontSize: 11, color: color.accent.primary, textDecoration: "none", fontVariantNumeric: "tabular-nums" },
  mdNoPhone: { fontSize: 10, color: color.text.muted },
  mdTags: { display: "flex", alignItems: "center", gap: 4, flexShrink: 0 },
  mdLeave: { fontSize: 10, color: color.text.muted, whiteSpace: "nowrap" },

  freeRow: { display: "flex", alignItems: "center", gap: 6, padding: "5px 6px" },
  freeName: { fontSize: font.size.body, fontWeight: font.weight.semibold },

  tag: {
    fontSize: 9,
    fontWeight: font.weight.bold,
    letterSpacing: ".4px",
    border: `1px solid ${color.border.default}`,
    borderRadius: radius.full,
    padding: "1px 6px",
    color: color.text.secondary,
    background: color.surface.raised,
    whiteSpace: "nowrap",
  },
  tagAic: { background: color.status.ready.bg, color: color.status.ready.text, borderColor: color.status.ready.border },
  tagBreak: { background: color.status.closing.bg, color: color.status.closing.text, borderColor: color.status.closing.border },
};
